import React from 'react';

const roles: { role: string; responsibility: string }[] = [
  {
    role: 'Chief Executive Officer (CEO)',
    responsibility:
      'Provides strategic oversight and allocates resources for continuity planning and implementation.',
  },
  {
    role: 'Data Protection Officer (DPO)',
    responsibility:
      'Ensures compliance with data protection mandates and coordinates privacy continuity during disruptions.',
  },
  {
    role: 'Incident Response and Continuity Team (IRCT)',
    responsibility:
      'Executes recovery protocols, manages communications, and restores business operations post-incident.',
  },
  {
    role: 'Department Heads',
    responsibility:
      'Maintain department-specific continuity playbooks and ensure personnel awareness of emergency procedures.',
  },
  {
    role: 'All Employees',
    responsibility:
      'Comply with continuity guidelines, report anomalies promptly, and assist in recovery and restoration efforts.',
  },
];

const BusinessContinuityPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#26ab4d]/25 via-black to-[#1e8a3f]/25"></div>
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(circle at 20% 30%, rgba(38, 171, 77, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(30, 138, 63, 0.15) 0%, transparent 50%)',
          }}
        ></div>
      </div>

      {/* Content */}
      <div className="relative z-10">
        <div className="container mx-auto px-[1.25rem] sm:px-[2rem] pt-[7rem] sm:pt-[9rem] lg:pt-[10rem] pb-[4rem] sm:pb-[6rem] lg:pb-[8rem]">
          <div className="max-w-4xl mx-auto text-white/85 leading-relaxed [overflow-wrap:anywhere]">
            {/* Header */}
            <div className="mb-10 sm:mb-12 border-b border-white/10 pb-6 sm:pb-8">
              <h1 className="text-[1.75rem] sm:text-[2.25rem] lg:text-[2.75rem] font-black text-white mb-3 leading-tight">
                Business Continuity{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f]">
                  Policy
                </span>
              </h1>
              <div className="space-y-1 text-[0.8125rem] sm:text-[0.9375rem] text-white/70">
                <p>
                  <span className="text-white/90 font-semibold">Entity Name:</span> Zarito Data
                  Solutions Private Limited
                </p>
                <p>
                  <span className="text-white/90 font-semibold">Operating Brand:</span> Bargad.ai
                </p>
                <p>
                  <span className="text-white/90 font-semibold">Last updated:</span> 1 July 2026
                </p>
              </div>
            </div>

            <div className="space-y-8 sm:space-y-10 text-[0.875rem] sm:text-[0.9375rem]">
              {/* 1. Purpose and Objective */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  1. Purpose and Objective
                </h2>
                <div className="space-y-3">
                  <p>
                    This Business Continuity Policy (BCP) establishes Bargad.ai’s strategic and
                    operational framework to sustain mission-critical functions, ensure data
                    integrity, and uphold client confidence during any form of disruption - whether
                    technological, environmental, or organizational.
                  </p>
                  <p>
                    The objective is to enable resilience through prevention, preparedness, and rapid
                    recovery, ensuring that essential systems, processes, and services remain
                    functional or are restored within acceptable timeframes, without compromising
                    security or compliance obligations.
                  </p>
                </div>
              </section>

              {/* 2. Scope */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  2. Scope
                </h2>
                <p className="mb-3">This policy extends to:</p>
                <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                  <li>
                    All operational and administrative functions of Zarito Data Solutions Private
                    Limited, encompassing data analytics, AI model operations, client onboarding, and
                    risk intelligence services.
                  </li>
                  <li>
                    All employees, contractual associates, and third-party service providers entrusted
                    with access to corporate or client data.
                  </li>
                  <li>
                    All digital assets, infrastructure, and applications forming part of the Bargad.ai
                    platform, including its APIs, cloud-based environments, and integrated monitoring
                    systems.
                  </li>
                </ul>
              </section>

              {/* 3. Policy Principles */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  3. Policy Principles
                </h2>
                <div className="space-y-5">
                  <div>
                    <h3 className="text-white font-semibold mb-1">
                      a. Risk Assessment and Business Impact Analysis
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                      <li>
                        Critical business functions and interdependencies are identified and
                        continuously monitored for potential vulnerabilities.
                      </li>
                      <li>
                        A Business Impact Analysis (BIA) is conducted at defined intervals to assess
                        the operational, financial, and reputational implications of service
                        interruptions.
                      </li>
                      <li>
                        Emerging threats - such as system compromise, network failure, or supplier
                        outage - are catalogued within a centralized Risk Register, with defined
                        mitigation and response measures.
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-white font-semibold mb-1">
                      b. Preventive and Mitigation Controls
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                      <li>
                        Multi-tiered safeguards including redundant systems, automated backups, and
                        real-time monitoring are maintained in accordance with the Information
                        Technology Act, 2000, and the Digital Personal Data Protection (DPDP) Act,
                        2023.
                      </li>
                      <li>
                        Rigorous access governance, encryption standards, and vulnerability
                        assessments are enforced to prevent data loss or unauthorized intrusion.
                      </li>
                      <li>
                        Preventive maintenance schedules are established for all core infrastructure
                        components to minimize avoidable downtime.
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-white font-semibold mb-1">
                      c. Business Continuity and Recovery Strategy
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                      <li>
                        All mission-critical workloads are deployed on redundant, geo-distributed
                        cloud architectures to enable immediate failover.
                      </li>
                      <li>
                        Defined Recovery Time Objectives (RTOs) and Recovery Point Objectives (RPOs)
                        guide restoration priorities across functions.
                      </li>
                      <li>
                        Disaster Recovery (DR) simulations are conducted periodically to validate
                        infrastructure resilience and employee readiness.
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-white font-semibold mb-1">
                      d. Communication and Crisis Coordination
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                      <li>
                        The Incident Response and Continuity Team (IRCT) is activated upon detection of
                        a major disruption.
                      </li>
                      <li>
                        The team ensures structured internal communication, timely client
                        notifications, and escalation to executive leadership or regulators, as
                        warranted.
                      </li>
                      <li>
                        Designated spokespersons handle all external communications to maintain
                        consistency, accuracy, and confidentiality.
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-white font-semibold mb-1">e. Data Backup and Restoration</h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                      <li>
                        Automated, encrypted backups of all critical systems are performed daily,
                        stored in secure offsite or multi-region cloud repositories.
                      </li>
                      <li>
                        Backup integrity is periodically validated through checksum verification and
                        controlled restoration exercises.
                      </li>
                      <li>
                        Restoration processes prioritize availability of fraud intelligence data and
                        API services to maintain continuity of client operations.
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-white font-semibold mb-1">
                      f. Vendor and Third-Party Continuity
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                      <li>
                        Engagements with vendors or subprocessors are contingent upon their adherence
                        to equivalent or superior continuity and data protection standards.
                      </li>
                      <li>
                        Annual audits and continuity assessments are performed to validate third-party
                        resilience frameworks.
                      </li>
                      <li>
                        All vendor agreements incorporate BCP and DR compliance clauses to ensure
                        mutual accountability in the event of service interruption.
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-white font-semibold mb-1">
                      g. Testing, Review, and Continuous Improvement
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                      <li>
                        BCP effectiveness is evaluated through periodic simulations and post-incident
                        reviews.
                      </li>
                      <li>
                        Findings and recommendations are incorporated into iterative improvements of
                        continuity plans and response procedures.
                      </li>
                      <li>
                        Policy updates reflect technological advancements, operational changes, and
                        emerging regulatory requirements.
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* 4. Roles and Responsibilities */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  4. Roles and Responsibilities
                </h2>
                <div className="space-y-3">
                  {roles.map((item) => (
                    <div
                      key={item.role}
                      className="rounded-lg border border-white/10 bg-white/[0.03] p-4 sm:p-5"
                    >
                      <div className="text-white font-semibold mb-1">{item.role}</div>
                      <div className="text-white/80">{item.responsibility}</div>
                    </div>
                  ))}
                </div>
              </section>

              {/* 5. Compliance and Governance */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  5. Compliance and Governance
                </h2>
                <p className="mb-3">This policy is governed and enforced in alignment with:</p>
                <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                  <li>Digital Personal Data Protection Act, 2023 (DPDP Act)</li>
                  <li>
                    Information Technology Act, 2000 and IT (Reasonable Security Practices and
                    Procedures) Rules, 2011
                  </li>
                  <li>
                    ISO 22301:2019 – Business Continuity Management Systems (BCMS) as an international
                    guiding framework
                  </li>
                </ul>
                <p className="mt-4 mb-3">Bargad.ai also ensures compliance through:</p>
                <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                  <li>Maintenance of consent-based data handling logs to preserve auditability.</li>
                  <li>
                    Documentation of all BCP and DR activities within an internal governance register.
                  </li>
                  <li>
                    Periodic review of risk posture and regulatory conformity by senior leadership.
                  </li>
                </ul>
              </section>

              {/* 6. Policy Review and Approval */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  6. Policy Review and Approval
                </h2>
                <div className="space-y-3">
                  <p>
                    This policy is subject to annual review, or earlier if prompted by major
                    operational, environmental, or regulatory developments.
                  </p>
                  <p>
                    The updated version shall be approved by executive management and circulated across
                    all relevant departments. A summarized version may also be made available for
                    client or regulatory examination upon request.
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessContinuityPolicy;
